import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  Navigate,
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import "./styles/App.css";
import { isEmpty } from "./functions/isEmpty";
import useFetch from "./functions/useFetch";
import io from "socket.io-client";

import Home from "./screens/Home";
import * as authentication from "./functions/authentication";
import useScreenType from "./functions/useScreenType";
import { LanguageProvider } from "./contexts/LanguageContext";
import Bottombar from "./components/Bottombar";
import Chat from "./screens/Chat";
import Menu from "./screens/Menu";
import Edit from "./screens/Edit";
import Automate from "./screens/Automate";
import Operator from "./screens/Operator";
import Login from "./screens/Login";
import Inbox from "./screens/Inbox";
import Finance from "./screens/Finance";
import Marketing from "./screens/Marketing";
import Calendar from "./screens/Calendar";
import ConnectEmail from "./screens/ConnectEmail";

const developerMode = true;

export default function App() {
  let apiURL = "https://cloud.ditochat.com";
  if (developerMode) {
    apiURL = "https://cloud.ditochat.com";
  }

  /* user Data */
  const [currentDito, setCurrentDito] = useState(null);

  /* user Auth */
  const [user, setUser] = useState(null);

  /* Login Data */
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");

  /* loading */
  const [reloadAll, setReloadAll] = useState(false);
  const [reloadConnections, setReloadConnections] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  /* screen */
  const isMobile = useScreenType();
  const [searchString, setSearchString] = useState("");
  const [scrollPositionHome, setScrollPositionHome] = useState(0);
  const [activeOperator, setActiveOperator] = useState(null);
  const [activeContact, setActiveContact] = useState(null);
  const [redirectionPlusOne, setRedirectionPlusOne] = useState(false);

  /* initialize */
  useEffect(() => {
    let authData = localStorage.getItem("@ditochat_user");
    if (!isEmpty(authData)) {
      setUser(JSON.parse(authData));
    }
  }, []);

  const {
    data: chats,
    loading: loadingChats,
    error: errorChats,
    setData: setChats,
  } = useFetch(
    `${apiURL}/api/chats/index.php?users_id=` + user?.id,
    null,
    "GET",
    null,
    [reloadAll, user],
    isEmpty(user) ? true : false
  );

  const {
    data: operators,
    loading: loadingOperators,
    error: errorOperators,
    setData: setOperators,
  } = useFetch(`${apiURL}/api/operators/index.php`, null, "GET", null, [
    reloadAll,
    user,
  ]);

  const {
    data: customers,
    loading: loadingCustomers,
    error: errorCustomers,
    setData: setCustomers,
  } = useFetch(
    `${apiURL}/api/customers/index.php?users_id=` + user?.id,
    null,
    "GET",
    null,
    [reloadAll, user],
    isEmpty(user) ? true : false
  );

  const {
    data: files,
    loading: loadingFiles,
    error: errorFiles,
    setData: setFiles,
  } = useFetch(
    `${apiURL}/api/files/index.php?users_id=` + user?.id,
    null,
    "GET",
    null,
    [reloadAll, user],
    isEmpty(user) ? true : false
  );

  const {
    data: plans,
    loading: loadingPlans,
    error: errorPlans,
    setData: setPlans,
  } = useFetch(
    `${apiURL}/api/plans/index.php?users_id=` + user?.id,
    null,
    "GET",
    null,
    [reloadAll, user],
    isEmpty(user) ? true : false
  );

  /*
  const {
    data: messages,
    loading: loadingMessages,
    error: errorMessages,
    setData: setMessages,
  } = useFetch(
    `${apiURL}/api/messages/getAllMessages.php?user_id=` + user?.id,
    null,
    "GET",
    null,
    [reloadAll, user],
    isEmpty(user) ? true : false
  );
  */

  // dies löschen sobald login implementiert wurde
  const {
    data: messages,
    loading: loadingMessages,
    error: errorMessages,
    setData: setMessages,
  } = useFetch(
    `${apiURL}/api/messages/index.php?users_id=` + user?.id,
    null,
    "GET",
    null,
    [reloadAll, user],
    isEmpty(user) ? true : false
  );

  const {
    data: connections,
    loading: loadingConnections,
    error: errorConnections,
    setData: setConnections,
  } = useFetch(
    `${apiURL}/api/connections/index.php?users_id=` + user?.id,
    null,
    "GET",
    null,
    [reloadAll, user, reloadConnections],
    isEmpty(user) ? true : false
  );

  const {
    data: automations,
    loading: loadingAutomations,
    error: errorAutomations,
    setData: setAutomations,
  } = useFetch(
    `${apiURL}/api/automations/index.php?users_id=` + user?.id,
    null,
    "GET",
    null,
    [reloadAll, user],
    isEmpty(user) ? true : false
  );

  useEffect(() => {
    if (searchString.length > 2) {
      let helpArr = [];
    }
  }, [searchString]);

  /* Websocket */
  const socketRef = useRef(null);
  const prevChatsRef = useRef([]);
  const chatsRef = useRef(chats);

  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  // Socket initialisieren (nur einmal)
  useEffect(() => {
    socketRef.current = io("wss://ws.ditochat.com", {
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    // Beim Verbinden bzw. Neuverbinden alle aktuellen Chat-Räume joinen
    socketRef.current.on("connect", () => {
      console.log("Verbunden mit dem WebSocket-Server:", socketRef.current.id);
      // Alle aktuell vorhandenen Chats beitreten
      chatsRef.current.forEach((chat) => {
        socketRef.current.emit("joinChat", chat.id);
        console.log(`Joined room for chat ${chat.id}`);
      });
    });

    socketRef.current.on("newMessage", (message) => {
      console.log("Neue Nachricht empfangen:", message);
      setMessages((prevMessages) => {
        // Finde den Index der Nachricht mit derselben ID, falls vorhanden
        const index = prevMessages.findIndex((msg) => msg.id === message.id);
        if (index !== -1) {
          // Ersetze die existierende Nachricht
          const updatedMessages = [...prevMessages];
          updatedMessages[index] = message;
          return updatedMessages;
        } else {
          // Hänge die neue Nachricht an, wenn sie noch nicht existiert
          return [...prevMessages, message];
        }
      });
    });

    socketRef.current.on("disconnect", () => {
      console.log("Verbindung zum WebSocket-Server getrennt");
    });

    return () => {
      socketRef.current.off("newMessage");
      socketRef.current.off("connect");
      socketRef.current.off("disconnect");
      socketRef.current.disconnect();
    };
  }, []); // Keine Abhängigkeiten – initialer Aufbau einmalig

  // Reagiere auf Änderungen im chats-State (z.B. neue Chats oder entfernte Chats)
  useEffect(() => {
    const prevChatIds = new Set(prevChatsRef.current.map((chat) => chat.id));
    const currentChatIds = new Set(chats.map((chat) => chat.id));

    // Neue Chats beitreten
    const newChats = chats.filter((chat) => !prevChatIds.has(chat.id));
    newChats.forEach((chat) => {
      socketRef.current.emit("joinChat", chat.id);
      console.log(`Joined room for chat ${chat.id}`);
    });

    // Falls nötig: Chats verlassen, die nicht mehr in currentChats sind
    const removedChats = prevChatsRef.current.filter(
      (chat) => !currentChatIds.has(chat.id)
    );
    removedChats.forEach((chat) => {
      socketRef.current.emit("leaveChat", chat.id);
      console.log(`Left room for chat ${chat.id}`);
    });

    prevChatsRef.current = chats;
  }, [chats]);

  // Beispiel: Neue Chats hinzufügen (dies könnte durch einen API-Call oder ein Formular ausgelöst werden)
  const addNewChat = (newChat) => {
    setChats((prevChats) => [...prevChats, newChat]);
  };

  return (
    <div className="app">
      <LanguageProvider>
        <Router>
          <Bottombar />
          <Routes>
            <Route
              path="/"
              element={
                <Home
                  isMobile={isMobile}
                  apiURL={apiURL}
                  isLoading={isUploading}
                  user={user}
                  chats={chats}
                  searchString={searchString}
                  setSearchString={setSearchString}
                  scrollPositionHome={scrollPositionHome}
                  setScrollPositionHome={setScrollPositionHome}
                  activeOperator={activeOperator}
                  setActiveOperator={setActiveOperator}
                  messages={messages}
                  operators={operators}
                  setActiveContact={setActiveContact}
                  plans={plans}
                />
              }
            />
            <Route
              path="/tasks"
              element={
                <Automate
                  isMobile={isMobile}
                  apiURL={apiURL}
                  isLoading={isUploading}
                  user={user}
                  chats={chats}
                  searchString={searchString}
                  setSearchString={setSearchString}
                  scrollPositionHome={scrollPositionHome}
                  setScrollPositionHome={setScrollPositionHome}
                  messages={messages}
                  operators={operators}
                  activeOperator={activeOperator}
                  setActiveOperator={setActiveOperator}
                  connections={connections}
                  automations={automations}
                  setAutomations={setAutomations}
                  setActiveContact={setActiveContact}
                />
              }
            />
            <Route
              path="/connectemail"
              element={
                <ConnectEmail
                  isMobile={isMobile}
                  apiURL={apiURL}
                  isLoading={isUploading}
                  user={user}
                  chats={chats}
                  searchString={searchString}
                  setSearchString={setSearchString}
                  scrollPositionHome={scrollPositionHome}
                  setScrollPositionHome={setScrollPositionHome}
                  messages={messages}
                  operators={operators}
                  activeOperator={activeOperator}
                  setActiveOperator={setActiveOperator}
                  connections={connections}
                  automations={automations}
                  setAutomations={setAutomations}
                  setActiveContact={setActiveContact}
                  setReloadConnections={setReloadConnections}
                  reloadConnections={reloadConnections}
                />
              }
            />
            <Route
              path="/inbox"
              element={
                <Inbox
                  isMobile={isMobile}
                  apiURL={apiURL}
                  isLoading={isUploading}
                  user={user}
                  chats={chats}
                  searchString={searchString}
                  setSearchString={setSearchString}
                  scrollPositionHome={scrollPositionHome}
                  setScrollPositionHome={setScrollPositionHome}
                  messages={messages}
                  files={files}
                  setFiles={setFiles}
                  operators={operators}
                  activeOperator={activeOperator}
                  setActiveOperator={setActiveOperator}
                  customers={customers}
                  activeContact={activeContact}
                  setActiveContact={setActiveContact}
                  connections={connections}
                  setReloadConnections={setReloadConnections}
                  reloadConnections={reloadConnections}
                />
              }
            />
            <Route
              path="/finance"
              element={
                <Finance
                  isMobile={isMobile}
                  apiURL={apiURL}
                  isLoading={isUploading}
                  user={user}
                  chats={chats}
                  searchString={searchString}
                  setSearchString={setSearchString}
                  scrollPositionHome={scrollPositionHome}
                  setScrollPositionHome={setScrollPositionHome}
                  messages={messages}
                  files={files}
                  setFiles={setFiles}
                  operators={operators}
                  activeOperator={activeOperator}
                  setActiveOperator={setActiveOperator}
                  customers={customers}
                  activeContact={activeContact}
                  setActiveContact={setActiveContact}
                  connections={connections}
                />
              }
            />
            <Route
              path="/marketing"
              element={
                <Marketing
                  isMobile={isMobile}
                  apiURL={apiURL}
                  isLoading={isUploading}
                  user={user}
                  chats={chats}
                  searchString={searchString}
                  setSearchString={setSearchString}
                  scrollPositionHome={scrollPositionHome}
                  setScrollPositionHome={setScrollPositionHome}
                  messages={messages}
                  files={files}
                  setFiles={setFiles}
                  operators={operators}
                  activeOperator={activeOperator}
                  setActiveOperator={setActiveOperator}
                  customers={customers}
                  activeContact={activeContact}
                  setActiveContact={setActiveContact}
                  connections={connections}
                />
              }
            />
            <Route
              path="/calendar"
              element={
                <Calendar
                  isMobile={isMobile}
                  apiURL={apiURL}
                  isLoading={isUploading}
                  user={user}
                  chats={chats}
                  searchString={searchString}
                  setSearchString={setSearchString}
                  scrollPositionHome={scrollPositionHome}
                  setScrollPositionHome={setScrollPositionHome}
                  messages={messages}
                  files={files}
                  setFiles={setFiles}
                  operators={operators}
                  activeOperator={activeOperator}
                  setActiveOperator={setActiveOperator}
                  customers={customers}
                  activeContact={activeContact}
                  setActiveContact={setActiveContact}
                  connections={connections}
                />
              }
            />
            <Route
              path="/chats/:id"
              element={
                <Chat
                  isMobile={isMobile}
                  apiURL={apiURL}
                  isLoading={isUploading}
                  user={user}
                  chats={chats}
                  setChats={setChats}
                  searchString={searchString}
                  setSearchString={setSearchString}
                  activeOperator={activeOperator}
                  setActiveOperator={setActiveOperator}
                  scrollPositionHome={scrollPositionHome}
                  setScrollPositionHome={setScrollPositionHome}
                  messages={messages}
                  setMessages={setMessages}
                  redirectionPlusOne={redirectionPlusOne}
                  setRedirectionPlusOne={setRedirectionPlusOne}
                  activeContact={activeContact}
                  setActiveContact={setActiveContact}
                  plans={plans}
                />
              }
            />
            <Route
              path="/menu"
              element={
                <Menu
                  isMobile={isMobile}
                  apiURL={apiURL}
                  isLoading={isUploading}
                  user={user}
                  chats={chats}
                  searchString={searchString}
                  setSearchString={setSearchString}
                  scrollPositionHome={scrollPositionHome}
                  setScrollPositionHome={setScrollPositionHome}
                  messages={messages}
                  setUser={setUser}
                />
              }
            />
            <Route
              path="/edit"
              element={
                <Edit
                  isMobile={isMobile}
                  apiURL={apiURL}
                  isLoading={isUploading}
                  user={user}
                  chats={chats}
                  searchString={searchString}
                  setSearchString={setSearchString}
                  scrollPositionHome={scrollPositionHome}
                  setScrollPositionHome={setScrollPositionHome}
                  messages={messages}
                />
              }
            />
            <Route
              path="/login"
              element={
                <Login
                  isMobile={isMobile}
                  apiURL={apiURL}
                  isLoading={isUploading}
                  user={user}
                  setUser={setUser}
                  chats={chats}
                  searchString={searchString}
                  setSearchString={setSearchString}
                  scrollPositionHome={scrollPositionHome}
                  setScrollPositionHome={setScrollPositionHome}
                  messages={messages}
                  email={email}
                  setEmail={setEmail}
                  password={password}
                  setPassword={setPassword}
                  username={username}
                  setUsername={setUsername}
                />
              }
            />
          </Routes>
          <></>
        </Router>
      </LanguageProvider>
    </div>
  );
}
